import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { fmtDate, COMPANY } from "@/lib/helpers";

function fmtUSD(n) {
  if (typeof n === "string") return n;
  return `$ ${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default function InvoicePrint() {
  const { id } = useParams();
  const [inv, setInv] = useState(null);
  useEffect(() => {
    (async () => {
      try { const { data } = await api.get(`/invoices/${id}`); setInv(data); } catch (e) { if (process.env.NODE_ENV !== "production") console.debug("non-fatal:", e); }
    })();
  }, [id]);
  useEffect(() => { if (inv) setTimeout(() => window.print(), 500); }, [inv]);

  if (!inv) return <div className="p-10">Loading…</div>;

  return (
    <div className="print-area max-w-4xl mx-auto p-10 bg-white text-black">
      <div className="no-print mb-6 flex items-center justify-between">
        <div className="phizo-label">Print Preview</div>
        <button onClick={() => window.print()} className="phizo-btn-primary">Print / Save PDF</button>
      </div>

      <div className="flex items-start justify-between pb-6 border-b-4 border-[#09090b]">
        <div className="flex items-center gap-4">
          <img src={COMPANY.logo} alt="Phizo" className="h-16 w-16 object-contain" />
          <div>
            <div className="font-heading text-3xl font-black tracking-tighter">{COMPANY.name}</div>
            <div className="text-sm text-[#52525b]">{COMPANY.address}</div>
          </div>
        </div>
        <div className="text-right">
          <div className="phizo-label">Invoice</div>
          <div className="font-heading text-4xl font-black tracking-tighter mono">{inv.number}</div>
          <div className="text-xs text-[#52525b] mt-1">Issued {fmtDate(inv.created_at)}</div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-6 py-6">
        <div>
          <div className="phizo-label mb-2">Bill To</div>
          <div className="font-semibold text-lg">{inv.customer}</div>
          {inv.customer_code && <div className="text-xs text-[#a1a1aa] mono mt-1">Code: {inv.customer_code}</div>}
          {inv.customer_address && <div className="text-sm text-[#52525b] whitespace-pre-line mt-1">{inv.customer_address}</div>}
        </div>
        <div className="text-right">
          <div className="phizo-label mb-2">Period</div>
          <div className="font-semibold">{fmtDate(inv.period_start)} → {fmtDate(inv.period_end)}</div>
          <div className="phizo-label mt-3 mb-1">Currency</div>
          <div className="font-semibold mono">USD</div>
        </div>
      </div>

      <table className="w-full border-t-2 border-b-2 border-[#09090b]">
        <thead>
          <tr className="border-b border-[#e4e4e7]">
            <th className="text-left py-3 phizo-label">DN #</th>
            <th className="text-left py-3 phizo-label">Date</th>
            <th className="text-left py-3 phizo-label">Material</th>
            <th className="text-left py-3 phizo-label">Truck</th>
            <th className="text-right py-3 phizo-label">Tonnes</th>
            <th className="text-right py-3 phizo-label">Rate</th>
            <th className="text-right py-3 phizo-label">Line Total</th>
          </tr>
        </thead>
        <tbody>
          {inv.lines.map((l, idx) => (
            <tr key={idx} className="border-b border-[#e4e4e7]">
              <td className="py-2 mono text-xs">{l.delivery_note_number}</td>
              <td className="py-2 text-xs">{fmtDate(l.delivery_date)}</td>
              <td className="py-2 text-sm">{l.product_name}</td>
              <td className="py-2 mono text-xs">{l.truck_plate || "—"}</td>
              <td className="py-2 text-right mono">{l.tonnes}</td>
              <td className="py-2 text-right mono">{fmtUSD(l.rate_usd)}</td>
              <td className="py-2 text-right mono font-semibold">{fmtUSD(l.line_total)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="flex justify-end py-6">
        <div className="w-80 space-y-2">
          <div className="flex justify-between text-sm">
            <span className="phizo-label">Subtotal</span>
            <span className="mono">{fmtUSD(inv.subtotal_usd)}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="phizo-label">Tax ({inv.tax_rate}%)</span>
            <span className="mono">{fmtUSD(inv.tax_usd)}</span>
          </div>
          <div className="flex justify-between pt-3 border-t-2 border-[#09090b]">
            <span className="phizo-label">Total Due (USD)</span>
            <span className="font-heading font-black text-2xl">{fmtUSD(inv.total_usd)}</span>
          </div>
        </div>
      </div>

      {inv.notes && (
        <div className="pt-6 border-t border-[#e4e4e7]">
          <div className="phizo-label mb-2">Notes</div>
          <div className="text-sm text-[#52525b] whitespace-pre-line">{inv.notes}</div>
        </div>
      )}

      <div className="pt-6 mt-6 border-t border-[#e4e4e7] text-xs text-[#52525b] grid grid-cols-2 gap-6">
        <div>
          <div className="phizo-label mb-1">Payment Instructions</div>
          <div>Please reference invoice {inv.number} on payment.</div>
          <div className="mt-1">Bank details to be supplied separately.</div>
        </div>
        <div className="text-right">
          <div className="phizo-label mb-1">Issued By</div>
          <div className="font-semibold">{inv.created_by_name}</div>
          <div>{COMPANY.name}</div>
        </div>
      </div>

      <div className="text-center text-xs text-[#a1a1aa] mt-10">
        Lines auto-generated from confirmed delivery notes. Material crushed and delivered as per signed records.
      </div>
    </div>
  );
}
